import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type ListProductsResult } from '@whinerp/client';
import { EmptyState, ErrorState, Num, Skeleton, type Column } from '@whinerp/ui';
import { useNavigate } from 'react-router-dom';
import { useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';
import { RecordList } from '../shared/record-list.tsx';
import { nextSort } from '../shared/use-saved-view.js';

/**
 * SC-55 Products list.
 *
 * The variant grid was reachable at /products/:id/variants and nothing
 * listed the catalogue, so the only route to a product was a quote line.
 *
 * The price is formatted by the one money formatter and carries its
 * currency: a bare number in a catalogue is the figure somebody quotes.
 */

type Row = ListProductsResult['products'][number];

function ProductsListScreen(): JSX.Element {
  const t = useT();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ field: string; direction: 'asc' | 'desc' }>({ field: 'name', direction: 'asc' });
  const [cursorStack, setCursorStack] = useState<string[]>([]);

  const query = useQuery<ListProductsResult, ApiError>({
    queryKey: ['products', 'all'],
    queryFn: () => api.listProducts({ query: {} }),
  });

  /*
   * Narrowed and ordered here rather than by the server.
   *
   * A workspace has tens of these, not tens of thousands. Paging them would
   * mean a cursor, a total and a page size describing a list that fits on one
   * screen - and the toolbar's search is honest about what it does: it
   * narrows what is already loaded.
   */
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const all = query.data?.products ?? [];
    const found = term === '' ? all : all.filter((row) => row.name.toLowerCase().includes(term) || row.sku.toLowerCase().includes(term));
    const direction = sort.direction === 'asc' ? 1 : -1;
    return [...found].sort((a, b) => sort.field === 'sku'
        ? a.sku.localeCompare(b.sku) * direction
        : a.name.localeCompare(b.name) * direction);
  }, [query.data, search, sort]);

  const columns = useMemo<Column<Row>[]>(
    () => [
      {
        key: 'name',
        header: t('list.column.name'),
        sortField: 'name',
        essential: true,
        cell: (row) => row.name,
      },
      {
        key: 'sku',
        header: t('list.column.sku'),
        sortField: 'sku',
        essential: true,
        cell: (row) => row.sku,
      },
      {
        key: 'price',
        header: t('list.column.price'),
        align: 'end',
        cell: (row) => <Num align="end" className="text-ink">{row.listPrice}</Num>,
      },
    ],
    [t],
  );

  return (
    <RecordList<Row>
      title={t('list.products.title')}
      icon="Package"
      rowId={(row) => row.productId}
      // The editor, not the variant grid. A row leads to the thing it is;
      // variants are one part of a product and reached from inside it.
      recordHref={(row) => `/products/${row.productId}/edit`}
      columns={columns}
      page={{ rows, total: rows.length, hasMore: false, nextCursor: null }}
      loading={query.isPending}
      error={query.isError ? { message: query.error.message, rule: query.error.rule } : null}
      onRetry={() => void query.refetch()}
      refreshing={query.isFetching}
      search={search}
      onSearchChange={setSearch}
      sort={sort}
      onSortChange={(field) => setSort((current) => nextSort(current, field))}
      cursorStack={cursorStack}
      onCursorStackChange={setCursorStack}
      pageSize={rows.length || 1}
      onNew={() => navigate('/products/new/edit')}
      onClearFilters={() => setSearch('')}
      filtered={search.trim().length > 0}
      emptyTitle={t('list.products.empty.title')}
      emptyBody={t('list.products.empty.body')}
      emptyIllustration="Package"
    />
  );
}

function ProductsListEmpty(): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason="no-records"
      illustration="Package"
      title={t('list.products.empty.title')}
      body={t('list.products.empty.body')}
      primary={{ label: t('product.editor.new'), icon: 'Plus', href: '/products/new/edit' }}
    />
  );
}

function ProductsListLoading(): JSX.Element {
  return (
    <div className="flex flex-col gap-px" role="status" aria-busy="true">
      <span className="sr-only">Loading</span>
      {Array.from({ length: 6 }, (_, index) => (
        <Skeleton key={index} className="h-row-comfortable" />
      ))}
    </div>
  );
}

function ProductsListError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="Products could not be loaded"
      body={error instanceof Error ? error.message : 'The list did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-55',
  route: '/products',
  surface: 'page',
  title: 'Products list',
  // F-046: `listProducts` is part of the quoting surface, which is what owns
  // the catalogue read.
  features: ['F-046'],
  entitlement: 'F-046',
  reachedBy: [{ kind: 'nav', item: 'products' }],
  components: ['DataTable', 'FilterChips'],
  elements: ['RecordList'],
  states: {
    empty: ProductsListEmpty,
    loading: ProductsListLoading,
    error: ProductsListError,
  },
  component: ProductsListScreen,
});

export default screen;
